import { Box, CircularProgress, Typography, SxProps, Theme } from '@mui/material'

export interface LoadingStateProps {
  /**
   * Optional loading message to display below the spinner
   */
  message?: string

  /**
   * Size of the spinner
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large' | number

  /**
   * Minimum height for the loading container
   * Useful for preventing layout shifts
   */
  minHeight?: string | number

  /**
   * Whether to center vertically (uses flex)
   * @default true
   */
  center?: boolean

  /**
   * Vertical padding
   * @default 4
   */
  py?: number

  /**
   * Additional styles
   */
  sx?: SxProps<Theme>
}

const sizeMap = {
  small: 24,
  medium: 40,
  large: 56,
}

/**
 * LoadingState - A consistent component for displaying loading states
 *
 * Shows a centered circular progress indicator with optional message,
 * useful for indicating that data is being fetched or processed.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <LoadingState />
 *
 * // With message
 * <LoadingState message="Loading data..." />
 *
 * // Large spinner with custom height
 * <LoadingState
 *   size="large"
 *   message="Processing..."
 *   minHeight={400}
 * />
 *
 * // Small spinner, no centering
 * <LoadingState
 *   size="small"
 *   center={false}
 *   py={2}
 * />
 * ```
 */
export function LoadingState({
  message,
  size = 'medium',
  minHeight,
  center = true,
  py = 4,
  sx,
}: LoadingStateProps) {
  const spinnerSize = typeof size === 'number' ? size : sizeMap[size]

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: center ? 'center' : 'flex-start',
        py,
        minHeight,
        gap: 2,
        ...sx,
      }}
    >
      <CircularProgress
        size={spinnerSize}
        sx={{ color: 'var(--vs-link)' }}
        thickness={4}
      />
      {message && (
        <Typography
          variant="body2"
          sx={{
            color: 'var(--vs-text-muted)',
            textAlign: 'center',
            maxWidth: 360,
            px: 'var(--space-4)',
          }}
        >
          {message}
        </Typography>
      )}
    </Box>
  )
}
